/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isNone } from '@ember/utils';
import { waitForEvent } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { retrieveOutgoingEdges } from 'cio/utils/visual-workflow-builder/branching';
import { DragMode, DropTargetItem, DropType } from 'cio/utils/visual-workflow-builder/types';
import { Events, FlyDragEvent } from 'fly-drag-drop/utils/events';
import EnvironmentService from 'cio/services/environment';
import FlyDragDropManagerService from 'fly-drag-drop/services/fly-drag-drop-manager';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import ActionSubjectsService from 'cio/services/action-subjects';
import DS from 'ember-data';
import CampaignModel from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import { tracked } from '@glimmer/tracking';
import EventBusService, { VWB_CANCEL_MOVE } from 'cio/services/event-bus';

const CONFIRMED_MOVING_DELAY = 'CONFIRMED_MOVING_DELAY';

interface DropContentArgs {
  index: number;
  isEmpty: boolean;
  isEditing?: boolean;
  isSaving?: boolean;
  allowDrop: boolean;
  lastIndex: boolean;
  campaign: CampaignModel;
  parentBranchId: string;
  isValidReconnectPointForExit: boolean;
  parentActions: string[];
  pathIndex: number;
  selectedAction: ActionModel | null;
  onDropItem: DropTargetItem;
  onDropType: DropType;
  onDrop: (event: FlyDragEvent) => unknown;
  onDragOver?: (event: FlyDragEvent) => unknown;
  onDragLeave?: (event: FlyDragEvent) => unknown;
  onCanDropChange?: (canDrop: boolean) => unknown;
  isCopyingHighlightedActions: boolean;
  isMovingHighlightedActions: boolean;
  isSelectingDropAreaThroughClick: boolean;
  // The VisualWorkflowBuilderV2 component (as opposed to the
  // VisualWorkflowBuilder) has a slightly different setup when it comes to what
  // happens after a drop has been performed (more logic lives in the VWB
  // component now to try and keep things in one place when it makes sense to do
  // so).
  // This flag is passed in from VWB V2 to make sure we short circuit the
  // drop/click in the _onDrop method
  isVersion2VWB?: boolean;
  onDropOrClickDropArea: (dropItem: DropTargetItem, dropType: DropType, event: FlyDragEvent) => unknown;
  dragMode: DragMode;
  highlightedNodeIds: string[];

  branchRejoinDrop?: boolean;
  dragStartEvent: FlyDragEvent | null;
}

export default class DropContent extends Component<DropContentArgs> {
  @service environment!: EnvironmentService;
  @service flyDragDropManager!: FlyDragDropManagerService;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;
  @service actionSubjects!: ActionSubjectsService;
  @service store!: DS.Store;
  @service eventBus!: EventBusService;

  get canDrop() {
    return this.args.allowDrop && this._canDrop;
  }

  @tracked _canDrop = true;
  @tracked isDraggingOver = false;

  @computed('args.{isSelectingDropAreaThroughClick,isValidReconnectPointForExit}', 'hasSelectedExitAction')
  get isSelectingDropAreaThroughClick() {
    if (this.hasSelectedExitAction) {
      return this.args.isValidReconnectPointForExit;
    }

    return this.args.isSelectingDropAreaThroughClick;
  }

  @computed('args.{lastIndex,onDropType}')
  get isLastDropAreaInPath() {
    return this.args.onDropType === 'insertAtEdge' || this.args.lastIndex;
  }

  @computed('args.selectedAction.isExit')
  get hasSelectedExitAction() {
    if (isNone(this.args.selectedAction)) {
      return false;
    }

    return this.args.selectedAction.isExit;
  }

  changeCanDrop(canDrop: boolean) {
    if (this.args.onCanDropChange) {
      this.args.onCanDropChange(canDrop);
    }
    this._canDrop = canDrop;
  }

  @action
  onMoveOrCopyHighlightedChanged() {
    if (this.flyDragDropManager.isDragging && this.args.dragStartEvent != null) {
      this.onDragStart(this.args.dragStartEvent);
    } else if (
      (this.args.isCopyingHighlightedActions || this.args.isMovingHighlightedActions || this.hasSelectedExitAction) &&
      this.isSelectingDropAreaThroughClick
    ) {
      this.onDragStart();
    }
  }

  @action
  onDragStart(event?: FlyDragEvent) {
    if (event?.detail.context?.sticky || event?.detail.context?.disableDroppingOnVWB) {
      return this.changeCanDrop(false);
    }

    if (event?.detail.context?.newOrExisting === 'new' && event?.detail.context?.item.type === 'exit_action') {
      // Only allow adding new exits to empty branches or at the end of a path
      return this.changeCanDrop(this.args.onDropType === 'insertAtEdge' || this.args.lastIndex);
    }

    // Moving highlighted actions after clicking "Move" in the highlight panel
    if (this.args.isMovingHighlightedActions && this.args.isSelectingDropAreaThroughClick) {
      let movedHighlightedActionsIncludeAnyAncestorBranch = this.args.parentActions.some((id) =>
        this.args.highlightedNodeIds.includes(id),
      );

      if (movedHighlightedActionsIncludeAnyAncestorBranch) {
        return this.changeCanDrop(false);
      }
    }

    if (
      this.args.dragMode === DragMode.Copy ||
      (this.args.isCopyingHighlightedActions && this.isSelectingDropAreaThroughClick)
    ) {
      return this.changeCanDrop(true);
    }

    if (event?.detail.context.action?.isExit) {
      return this.changeCanDrop(true);
    }

    // Moving one or more actions via dragging
    if (
      (this.args.dragMode === DragMode.Move ||
        (this.args.isMovingHighlightedActions && this.args.isSelectingDropAreaThroughClick)) &&
      this.args.highlightedNodeIds.includes(event?.detail.context.action?.id)
    ) {
      let movedHighlightedActionsIncludeAnyAncestorBranch = this.args.parentActions.some((id) =>
        this.args.highlightedNodeIds.includes(id),
      );

      if (movedHighlightedActionsIncludeAnyAncestorBranch) {
        return this.changeCanDrop(false);
      }
    }

    if (event?.detail.context.action?.isBranch) {
      // Disabled the drop area if the currently dragged item is the drop area's
      // parent branch (or that branch's parent etc.)
      let isDraggedActionAncestorBranch = this.args.parentActions.includes(event.detail.context.action.id);

      if (isDraggedActionAncestorBranch) {
        return this.changeCanDrop(false);
      }
    }

    if (event?.detail.context.action?.isExit) {
      let branchAction = this.args.campaign.workflowActions.find((item) => item.id === event.detail.context.branchId);

      let isSameBranch = event?.detail.context.branchId === this.args.parentBranchId;
      let isDifferentPath = event?.detail.context.pathIndex !== this.args.pathIndex;

      if (branchAction) {
        if (
          this.args.selectedAction &&
          !isNone(this.args.campaign.branchRejoinPointsMap[this.args.selectedAction.id])
        ) {
          return this.changeCanDrop((this.args.branchRejoinDrop ?? false) && isSameBranch && isDifferentPath);
        }
      }

      return this.changeCanDrop(isSameBranch && isDifferentPath);
    }

    // Disable the drop areas immediately before and after the action that's
    // currently dragged
    if (this.args.onDropType === 'insertBefore') {
      let isDifferentAction =
        this.args.isMovingHighlightedActions || event?.detail.context.moveHighlighted
          ? !this.args.highlightedNodeIds.includes((this.args.onDropItem as any).action.id)
          : event?.detail.context.action?.id !== (this.args.onDropItem as any).action.id;

      return this.changeCanDrop(isDifferentAction);
    }

    // Disable if the dragged action is the same as the onDropItem, or if the
    // edge that's leaving the onDropItem connects to the dragged action.
    //
    // Imagine this workflow:
    // [ insertBefore action A ]
    // [ action A ]
    // [ insertAfter action A ]
    // [ action B ]
    // [ insertAfter action B ]
    //
    // If we drag action B, we need to disable "insertAfter action B", but we
    // also need to disable "insertAfter action A" - because that drop point is
    // right before "action B". So if action A points to action B, we disable
    // the action A insertAfter drop.
    //
    // Note that if the edge that points from A to B is actually an edge that
    // leaves a branch path, we need to keep this enabled:
    //                [ branch ]
    //                    |
    //          ----------------------
    //          |                    |
    // [ insertBefore A ]    [ insertBefore B ]
    //    [ action A ]          [ action B ]
    // [ insertAfter A ]     [ insertAfter B ]
    //          |                    |
    //          ----------------------
    //                    |
    //          [ insertAtBranchEnd ]
    //              [ action C ]
    //
    // If we drag action C, we still want to be able to drag it into insertAfter
    // A or B. Those edges point to C, but they are in different branches/paths
    // from action C, so we can keep them enabled.
    if (this.args.onDropType === 'insertAfter') {
      let edgesLeavingDropAreaAction = this.args.campaign.consolidatedTree.edges.filter(
        (edge) => edge.from === (this.args.onDropItem as any).action.id,
      );
      let edgeTargets = edgesLeavingDropAreaAction.map((item) => item.to);

      if (this.args.isMovingHighlightedActions || event?.detail.context.moveHighlighted) {
        // If multiple actions are being moved, we need to check a few
        // conditions:
        // - the drop area isn't between the highlighted actions (can't drop
        //   a group of actions into a spot that's within the group itself)
        // - when dragging multiple actions that directly follow a branch
        //   action, it should be possible to drop the actions anywhere within
        //   the branch action's paths
        //
        // Examples:
        //                [ branch ]
        //                    |
        //          ----------------------
        //          |                    |
        // [ insertBefore A ]    [ insertBefore B ]
        //    [ action A ]          [ action B ]
        // [ insertAfter A ]     [ insertAfter B ]
        //          |                    |
        //          ----------------------
        //                    |
        //          [ insertAtBranchEnd ]
        //              [ action C ]
        //            [ insertAfter C ]
        //                    |
        //              [ action D ]
        //            [ insertAfter D ]
        //
        // 1. If we drag actions C and D, we need to disable:
        //    - insertAtBranchEnd - because it's right next to action C
        //    - insertAfterC and insertAfterD - because they also connect to the
        //      highlighted actions
        //    However, insertAfterA and insertAfterB should remain enabled. Even
        //    though the edges technically point to C, they are in different
        //    branch paths, so the movement should be allowed.
        // 2. If we drag actions A and C, we need to disable:
        //    - insertBeforeA and insertAfterA - because they are next to action
        //      A
        //    - insertAtBranchEnd and insertAfterC - because they are next to
        //      action C
        //    Otherwise, all other areas should remain available. In this
        //    scenario, insertAfterB is an interesting edge case: it connects to
        //    insertAfterB, but is in a different branch action. It is in the
        //    same branch action as A though, but in a different path, which
        //    tells us the movement is safe.

        let isUnsafeDropNode = edgeTargets.some((target) => {
          // If the target of the drop action is not one of the highlighted
          // nodes, it's safe and we can move on to the next target check.
          if (!this.args.highlightedNodeIds.includes(target)) {
            return false;
          }

          // If the target of the drop action is one of the highlighted nodes,
          // then we need to check whether they're in different branches or
          // branch paths.
          let targetParentBranch = this.args.campaign.parentBranchMap[target];
          return targetParentBranch === this.args.parentBranchId;
        });

        return this.changeCanDrop(
          !this.args.highlightedNodeIds.includes((this.args.onDropItem as any).action.id) && !isUnsafeDropNode,
        );
      }

      let differentBranches = this.args.parentBranchId !== event?.detail.context.branchId;
      return this.changeCanDrop(
        event?.detail.context.action?.id !== (this.args.onDropItem as any).action.id &&
          (differentBranches || !edgeTargets.includes(event?.detail.context.action?.id)),
      );
    }

    //              [ branch 1 ]
    //                    |
    //          ----------------------
    //          |                    |
    //    [ action A ]          [ branch 2 ]
    //          |                    |
    //          |          ----------------------
    //          |          |                    |
    //          |          ----------------------
    //          |                    |
    //          |         [ insertAtBranchEnd 2 ]
    //          |                    |
    //          ----------------------
    //                    |
    //          [ insertAtBranchEnd 1 ]
    //                    |
    //              [ action C ]
    //
    // Imagine we're dragging action C.
    // We need to disable insertAtBranchEnd 1, because that's where the action
    // already lives.
    // However, insertAtBranchEnd 2 needs to stay enabled, because it still is
    // a valid drop point.
    if (this.args.onDropType === 'insertAtBranchEnd') {
      let branchId = (this.args.onDropItem as any).action.id;
      let branchReconnectPoint = this.args.campaign.branchRejoinPointsMap[branchId];

      let draggedActionIsNotReconnectPoint =
        this.args.isMovingHighlightedActions || event?.detail.context.moveHighlighted
          ? !this.args.highlightedNodeIds.includes(branchReconnectPoint)
          : branchReconnectPoint !== event?.detail.context.action?.id;

      if (draggedActionIsNotReconnectPoint) {
        return this.changeCanDrop(true);
      }

      // In the branchRejoinPointsMap, action C will be the rejoin points for
      // both branch 1 and branch 2, because that's the action both branches
      // directly connect to.
      // We need to check whether the current drop point is nested, but still
      // connecting to the same point, so that we can enable scenarios like
      // insertAtBranchEnd 2 described above.
      let parentBranchId = this.args.campaign.parentBranchMap[branchId];
      if (!isNone(parentBranchId)) {
        let parentBranchReconnectPoint = this.args.campaign.branchRejoinPointsMap[parentBranchId];
        let parentBranchHasSameReconnectPoint = branchReconnectPoint === parentBranchReconnectPoint;

        return this.changeCanDrop(parentBranchHasSameReconnectPoint);
      }

      return this.changeCanDrop(false);
    }

    return this.changeCanDrop(true);
  }

  @tracked actionSubjectsCount = 0;
  @tracked actionsCount = 0;
  @tracked isMovingBranch = false;

  // Waits for confirmation from a user before continuing with the drop when moving
  // delay actions that have subjects within or branches that contain delay actions
  // that have subjects within them.
  confirmMovingDelayTask = task(
    { drop: true },
    async (event: FlyDragEvent, actions: ActionModel[] = [], { isMovingBranch = false } = {}) => {
      let subjectsCount = actions
        .map((action) => this.actionSubjects.find(action.id))
        .filter((item) => item?.count ?? 0 > 0)
        .reduce((sum, item) => sum + (item?.count ?? 0), 0);

      if (subjectsCount > 0) {
        this.actionSubjectsCount = subjectsCount;
        this.actionsCount = actions.length;
        this.isMovingBranch = isMovingBranch;

        await waitForEvent(this.eventBus, CONFIRMED_MOVING_DELAY);
      }

      this.args.onDrop(event);
    },
  );

  @action
  onDragEnd() {
    this._canDrop = true;
  }

  willDestroy() {
    super.willDestroy();

    this.flyDragDropManager.off(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.off(Events.DragEnd, this.onDragEnd);
    this.eventBus.unsubscribe(VWB_CANCEL_MOVE, this, this.cancelMovingDelay);
  }

  @action
  didInsert() {
    this.flyDragDropManager.on(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.on(Events.DragEnd, this.onDragEnd);
    this.eventBus.subscribe(VWB_CANCEL_MOVE, this, this.cancelMovingDelay);
  }

  @action
  onDrop(event: FlyDragEvent) {
    if (this.args.isVersion2VWB) {
      return this.args.onDropOrClickDropArea(this.args.onDropItem, this.args.onDropType, event);
    }

    let { action } = event.detail.context.draggable;

    // For branches that have delay actions within them, we want to confirm that a user wants to move
    if (action?.isBranch) {
      let rejoinPointId = action.isBranch ? this.args.campaign.branchRejoinPointsMap[action.id] : null;

      let { traversedNodes } = retrieveOutgoingEdges(action, {
        edges: this.args.campaign.consolidatedTree.edges,
        reconnectNodeId: rejoinPointId,
      });

      let childDelayActions = (traversedNodes as string[])
        .map((id) => this.store.peekRecord('action', id))
        .filter((action) => action?.isDelay) as ActionModel[];

      if (childDelayActions.length > 0) {
        return this.confirmMovingDelayTask.perform(event, childDelayActions, { isMovingBranch: true });
      }
    }

    // For single delay-actions, we also want to confirm that a user wants to move
    if (action?.isDelay) {
      return this.confirmMovingDelayTask.perform(event, [action]);
    }

    if (event.detail.context.draggable.copy) {
      return event.detail.context.draggable.onAddCopied(this.args.onDropItem, this.args.onDropType, event);
    } else {
      this.args.onDrop(event);
    }
  }

  @action
  onClickDropButton() {
    this.args.onDropOrClickDropArea(this.args.onDropItem, this.args.onDropType, {
      detail: {
        context: { draggable: { action: this.args.selectedAction } },
      },
    } as any);

    if (this.hasSelectedExitAction) {
      this.visualWorkflowBuilder.resetExitToDelete();
    }
  }

  @action
  cancelMovingDelay() {
    this.actionSubjectsCount = 0;
    this.actionsCount = 0;
    this.isMovingBranch = false;
    this.isDraggingOver = false;

    this.confirmMovingDelayTask.cancelAll({ resetState: true });
  }

  @action
  confirmMovingDelay() {
    this.eventBus.trigger(CONFIRMED_MOVING_DELAY);
  }

  @action
  onDragEnter() {
    this.isDraggingOver = true;
  }

  @action
  onDragLeave(event: FlyDragEvent) {
    this.isDraggingOver = false;

    if (this.args.onDragLeave) {
      this.args.onDragLeave(event);
    }
  }
}
